import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import { Badge, Button, Card, LoadingSpinner, Modal, ModalFooter } from '@/components/common';
import { useInsurerPermissions } from '@/hooks/useInsurerPermissions';
import api, { getErrorMessage } from '@/services/api';

/**
 * #355: the insurer's Brokers page. Every broker connected to this insurer with
 * licence and business figures for THIS insurer only, the connection switch
 * (enable / disable) and the product switch (which products the broker may
 * sell). Approve / Reject Broker is the request workflow on
 * `/insurance/broker-requests` (linked from here). The commission columns wait
 * for insurer payment tracking (#444).
 */

export interface BrokerRow {
  broker_id: string;
  name: string;
  code: string;
  status: 'ACTIVE' | 'DISABLED_BY_INSURER' | 'DISABLED_BY_BROKER' | 'SUSPENDED';
  products_enabled: number;
  products_offered: number;
  policies: number;
  premium: string;
  licence_number: string;
  licence_valid_until: string | null;
  licence_state: 'valid' | 'expiring' | 'expired' | 'unknown';
  products?: Array<{ id: string; name: string; enabled: boolean; locked_by_broker: boolean }>;
}

const BASE = '/insurers/insurance/brokers/';
const TH = 'px-4 py-2 text-left text-xs font-semibold uppercase text-gray-600 dark:text-gray-300';
const TD = 'px-4 py-2 text-sm text-gray-900 dark:text-gray-100';
const STATUS_VARIANT = { ACTIVE: 'success', DISABLED_BY_INSURER: 'error', DISABLED_BY_BROKER: 'warning', SUSPENDED: 'warning' } as const;
const STATUS_LABEL = {
  ACTIVE: 'Active',
  DISABLED_BY_INSURER: 'Disabled by you',
  DISABLED_BY_BROKER: 'Disabled by broker',
  SUSPENDED: 'Suspended',
} as const;
const LICENCE_VARIANT = { valid: 'success', expiring: 'warning', expired: 'error', unknown: 'default' } as const;
const FILTERS = [{ value: '', label: 'All' }, ...Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }))];
const money = (value: string) => Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 });

export function BrokersPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const { can } = useInsurerPermissions();

  const { data, isLoading, isError } = useQuery<{ count: number; results: BrokerRow[] }>({
    queryKey: ['insurerBrokers', search, status],
    queryFn: async () =>
      (await api.get(BASE, { params: { ...(search ? { search } : {}), ...(status ? { status } : {}) } })).data,
  });
  const rows = data?.results ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Brokers"
        subtitle="Brokers connected to your company"
        actions={
          can('broker_requests.read') ? (
            <Link to="/insurance/broker-requests?status=pending" className="text-sm text-primary-600 hover:underline dark:text-primary-400">
              Approve or reject broker requests
            </Link>
          ) : undefined
        }
      />
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="broker-search" className="block text-sm text-gray-600 dark:text-gray-300">
            Search
          </label>
          <input
            id="broker-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name or code"
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
        </div>
        <div>
          <label htmlFor="broker-status" className="block text-sm text-gray-600 dark:text-gray-300">
            Status
          </label>
          <select
            id="broker-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          >
            {FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <Card className="p-4 sm:p-6">
        {isLoading ? (
          <LoadingSpinner />
        ) : isError ? (
          <p className="text-sm text-red-600 dark:text-red-400">Brokers could not be loaded.</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No brokers found</p>
        ) : (
          <>
            <table className="hidden min-w-full divide-y divide-gray-200 dark:divide-gray-700 md:table">
              <thead className="bg-gray-50 dark:bg-gray-900/40">
                <tr>
                  <th className={TH}>Broker</th>
                  <th className={TH}>Code</th>
                  <th className={TH}>Status</th>
                  <th className={TH}>Products</th>
                  <th className={TH}>Policies</th>
                  <th className={TH} title="Gross written premium of issued, non-cancelled policies on your paper">
                    Premium
                  </th>
                  <th className={TH}>Licence</th>
                  <th className={TH}>
                    <span className="sr-only">Open</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rows.map((r) => (
                  <tr key={r.broker_id}>
                    <td className={TD}>{r.name}</td>
                    <td className={TD}>{r.code}</td>
                    <td className={TD}>
                      <Badge variant={STATUS_VARIANT[r.status]} size="sm">
                        {STATUS_LABEL[r.status]}
                      </Badge>
                    </td>
                    <td className={TD}>
                      {r.products_enabled} of {r.products_offered}
                    </td>
                    <td className={TD}>{r.policies}</td>
                    <td className={TD}>{money(r.premium)}</td>
                    <td className={TD}>
                      <Badge variant={LICENCE_VARIANT[r.licence_state]} size="sm">
                        {r.licence_state}
                      </Badge>
                    </td>
                    <td className={TD}>
                      <Button size="sm" variant="outline" onClick={() => setOpenId(r.broker_id)}>
                        View {r.name}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <ul className="space-y-3 md:hidden">
              {rows.map((r) => (
                <li key={r.broker_id} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-gray-900 dark:text-gray-100">{r.name}</span>
                    <Badge variant={STATUS_VARIANT[r.status]} size="sm">
                      {STATUS_LABEL[r.status]}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {r.code} - {r.products_enabled} of {r.products_offered} products - {r.policies} policies
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300">Premium {money(r.premium)}</p>
                  <Button size="sm" variant="outline" className="mt-2" onClick={() => setOpenId(r.broker_id)}>
                    View {r.name}
                  </Button>
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>
      {openId && <BrokerDetail id={openId} canEdit={can('brokers.edit')} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function BrokerDetail({ id, canEdit, onClose }: { id: string; canEdit: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  // null = untouched: the checklist then mirrors the server.
  const [enabled, setEnabled] = useState<Set<string> | null>(null);

  const { data, isLoading, isError } = useQuery<BrokerRow>({
    queryKey: ['insurerBroker', id],
    queryFn: async () => (await api.get(`${BASE}${id}/`)).data,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['insurerBrokers'] });
    queryClient.invalidateQueries({ queryKey: ['insurerBroker', id] });
  };
  const onError = (error: unknown) => toast.error(getErrorMessage(error));

  const toggle = useMutation({
    mutationFn: async (action: 'enable' | 'disable') =>
      (await api.post(`${BASE}${id}/${action}/`, action === 'disable' && reason.trim() ? { reason: reason.trim() } : {})).data,
    onSuccess: (_d, action) => {
      toast.success(action === 'enable' ? 'Broker enabled' : 'Broker disabled');
      setConfirming(false);
      setReason('');
      refresh();
    },
    onError,
  });
  const save = useMutation({
    mutationFn: async (ids: string[]) => (await api.put(`${BASE}${id}/products/`, { product_ids: ids })).data,
    onSuccess: () => {
      toast.success('Products updated');
      setEnabled(null);
      refresh();
    },
    onError,
  });

  const products = data?.products ?? [];
  const current = enabled ?? new Set(products.filter((p) => p.enabled).map((p) => p.id));
  const setAll = (on: boolean) => setEnabled(new Set(on ? products.map((p) => p.id) : products.filter((p) => p.locked_by_broker).map((p) => p.id)));

  return (
    <>
      <Modal isOpen={!confirming} onClose={onClose} title={data ? data.name : 'Broker'} size="lg">
        {isLoading ? (
          <LoadingSpinner />
        ) : isError || !data ? (
          <p className="text-sm text-red-600 dark:text-red-400">This broker could not be loaded.</p>
        ) : (
          <div className="space-y-4 text-sm text-gray-900 dark:text-gray-100">
            <dl className="grid grid-cols-2 gap-2">
              <dt className="text-gray-500 dark:text-gray-400">Code</dt>
              <dd>{data.code}</dd>
              <dt className="text-gray-500 dark:text-gray-400">Connection</dt>
              <dd>
                <Badge variant={STATUS_VARIANT[data.status]} size="sm">
                  {STATUS_LABEL[data.status]}
                </Badge>
              </dd>
              <dt className="text-gray-500 dark:text-gray-400">Licence</dt>
              <dd>
                {data.licence_number || 'Not on file'}{' '}
                <Badge variant={LICENCE_VARIANT[data.licence_state]} size="sm">
                  {data.licence_state}
                </Badge>{' '}
                {data.licence_valid_until ?? ''}
              </dd>
              <dt className="text-gray-500 dark:text-gray-400">Policies with you</dt>
              <dd>{data.policies}</dd>
              <dt className="text-gray-500 dark:text-gray-400">Premium with you</dt>
              <dd>{money(data.premium)}</dd>
            </dl>
            {data.licence_state === 'expired' && (
              <p role="alert" className="rounded-md bg-amber-50 p-3 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                This broker&apos;s licence has expired. It cannot write new business until it is renewed.
              </p>
            )}
            <fieldset>
              <legend className="font-medium">Products this broker may sell</legend>
              {canEdit && products.length > 0 && (
                <div className="my-1 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setAll(true)}>
                    Select all
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setAll(false)}>
                    Clear
                  </Button>
                </div>
              )}
              {products.length === 0 && <p className="text-gray-500 dark:text-gray-400">No active products.</p>}
              {products.map((p) => (
                <label key={p.id} className="mt-1 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={current.has(p.id)}
                    disabled={!canEdit || p.locked_by_broker}
                    onChange={(e) => {
                      const next = new Set(current);
                      if (e.target.checked) next.add(p.id);
                      else next.delete(p.id);
                      setEnabled(next);
                    }}
                  />
                  {p.name}
                  {p.locked_by_broker && <span className="text-xs text-gray-500">(switched off by the broker)</span>}
                </label>
              ))}
            </fieldset>
            {canEdit && (
              <ModalFooter>
                {data.status === 'ACTIVE' || data.status === 'SUSPENDED' ? (
                  <Button variant="danger" onClick={() => setConfirming(true)}>
                    Disable broker
                  </Button>
                ) : data.status === 'DISABLED_BY_INSURER' ? (
                  <Button variant="outline" onClick={() => toggle.mutate('enable')} isLoading={toggle.isPending}>
                    Enable broker
                  </Button>
                ) : (
                  <span className="text-xs text-gray-500">Only the broker can switch this connection back on.</span>
                )}
                <Button
                  onClick={() => save.mutate([...current])}
                  disabled={enabled === null}
                  isLoading={save.isPending}
                >
                  Save products
                </Button>
              </ModalFooter>
            )}
          </div>
        )}
      </Modal>
      <Modal
        isOpen={confirming}
        onClose={() => setConfirming(false)}
        title="Disable broker"
        description="New quotes and policies are blocked. In-force policies, renewals and claims are not affected."
      >
        <label htmlFor="disable-reason" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          Reason (optional)
        </label>
        <textarea
          id="disable-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={500}
          className="mt-1 w-full rounded-md border border-gray-300 bg-white p-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        />
        <ModalFooter>
          <Button variant="outline" onClick={() => setConfirming(false)}>
            Cancel
          </Button>
          <Button variant="danger" isLoading={toggle.isPending} onClick={() => toggle.mutate('disable')}>
            Disable
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
